"use client"

import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ArrowRight } from "lucide-react"
import Link from "next/link"
import Image from "next/image"
import { useState, useEffect, useRef } from "react"

interface Course {
  id: string
  title: string
  subtitle: string
  description: string
  price: string
  duration: string
  classSize: string
  startDate: string
  image: string
  slug: string
  signupForm: string
  highlighted: boolean
}

interface CourseCardProps {
  course: Course
  index: number
}

export function CourseCard({ course, index }: CourseCardProps) {
  const [isVisible, setIsVisible] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.unobserve(entry.target)
          }
        })
      },
      { rootMargin: "100px", threshold: 0.01 }
    )
    if (cardRef.current) observer.observe(cardRef.current)
    return () => {
      if (cardRef.current) observer.unobserve(cardRef.current)
    }
  }, [])

  if (index < 3) return <CourseCardContent course={course} />
  if (!isVisible) return <div ref={cardRef} className="aspect-square min-h-[280px]" />
  return <CourseCardContent course={course} />
}

function CourseCardContent({ course }: { course: Course }) {
  return (
    <Card
      className={`group relative overflow-hidden rounded-2xl border border-border shadow transition-all duration-300 ${
        course.highlighted ? "bg-highlighted-course" : "bg-card"
      }`}
    >
      <CardContent className="p-0 flex flex-col">
        {course.highlighted && (
          <div className="absolute top-3 right-3 z-10">
            <Badge className="bg-destructive text-primary-foreground py-1 px-3">
              Онцлох
            </Badge>
          </div>
        )}

        <Link href={`/courses/${course.slug}`} className="block flex-1 no-underline">
          <div className="aspect-square w-full overflow-hidden">
            <Image
              src={course.image || "https://mbg.mn/og.jpg"}
              alt={course.title}
              width={400}
              height={400}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              style={{ objectPosition: "center" }}
              loading={course.highlighted ? "eager" : "lazy"}
              placeholder="blur"
              blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YzZjRmNiIvPjwvc3ZnPg=="
            />
          </div>
        </Link>

        <div className="p-4">
          <Link href={`/courses/${course.slug}`}>
            <Button className="w-full rounded-lg group/btn transition-all duration-300">
              Дэлгэрэнгүй
              <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

